import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ACTUALS, actualTab, FREQUENCY_BUDGET_APPROVAL, FREQUENCY_REAL_TIME, frequencyTabs, PLANNED, plannedFrequency, plannedTab } from "./consts";
import { useGetDistributionJobsList, useRunJobMutation, useUpdateJobStatusMutation } from "@/services";
import moment from "moment";
import { Badge, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, Label, Switch } from "@/components/ui";
import { cn, distributionJobStatus } from "@/lib/utils";
import { Box, Edit, Eye, MessageSquareText, MoreVertical } from "lucide-react";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { setDistributionJobDtl } from "@/redux/slices";

const useDistributionRunTimeDetails = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  const isEdit = useRef<boolean>(false);
  const selectedJobId = useRef<string>("");
  const communityName = communityData?.name || communityData?.communityName || "";
  const title = `Allocation Run Time Details - ${communityName}(${currentFinanceYear})`;

  const [activeFrequencyTab, setActiveFrequencyTab] = useState<string>(FREQUENCY_REAL_TIME)
  const [activeStatusTab, setActiveStatusTab] = useState(PLANNED);
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);

  const isPlanned = useMemo(() => activeStatusTab == PLANNED, [activeStatusTab]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: "",
        name: <span className="text-primary">Allocation Run Time Details</span>,
      },
    ],
    [communityName]
  );

  const onAddJobClick = () => {
    selectedJobId.current = ""
    isEdit.current = false;
    setOpenAddDialog(true)
  }

  const {data: jobList, isLoading: isLoadingJobList } = useGetDistributionJobsList({
    communityId: communityData?.communityId,
    frequency: isPlanned ? FREQUENCY_BUDGET_APPROVAL : activeFrequencyTab,
    type: activeStatusTab,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && activeFrequencyTab && activeStatusTab && currentFinanceYear)
    }
  })
  
  const onStatusTabChange = (value: any) => {
    setActiveStatusTab(value)
  }

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateJobStatusMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Job Status Updated Successfully");
        queryClient.invalidateQueries({
          queryKey: ["distribution-jobs-list", communityData?.communityId, currentFinanceYear, activeStatusTab, activeFrequencyTab],
        });
      },
    });

  const { mutate: runJobInfo, isPending: isRunJobPending } =
    useRunJobMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Job Run Successfully");
        queryClient.invalidateQueries({
          queryKey: ["distribution-jobs-list", communityData?.communityId, currentFinanceYear, activeStatusTab, activeFrequencyTab],
        });
      },
    });

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "distributionJobId",
        header: <div>Job ID</div>,
        accessorFn: (row: any) => String(row?.distributionJobId || ""),
      },
      {
        accessorKey: "runType",
        header: <div>Run Type</div>,
        accessorFn: (row: any) => [...frequencyTabs, ...plannedFrequency]?.find((data: any) => data?.value == row?.frequency)?.runType || "",
      },
      {
        accessorKey: "frequency",
        header: <div>Frequency</div>,
        accessorFn: (row: any) => [...frequencyTabs, ...plannedFrequency]?.find((data: any) => data?.value == row?.frequency)?.label || "",
      },
      {
        accessorKey: "scheduledOn",
        header: <div>Schedule On</div>,
        accessorFn: (row: any) => row?.scheduledOn ? moment.utc(row?.scheduledOn).local().format("MM/DD/YYYY HH:mm:ss") : "",
      },
      {
        accessorKey: "periodFrom",
        header: <div>Period From</div>,
        accessorFn: (row: any) => row?.periodFrom ? moment.utc(row?.periodFrom).local().format("MM/DD/YYYY HH:mm:ss") : "",
      },
      {
        accessorKey: "periodTo",
        header: <div>Period To</div>,
        accessorFn: (row: any) => row?.periodTo ? moment.utc(row?.periodTo).local().format("MM/DD/YYYY HH:mm:ss") : "",
      },
      {
        accessorKey: "status",
        header: <div>Status</div>,
        cell: ({row}: any) => (
          <Badge
            variant="outline"
            className={cn(
              distributionJobStatus[row?.original?.status?.toLowerCase()]?.color,
              "rounded-full px-4 py-2"
            )}
          >
            {distributionJobStatus[row?.original?.status?.toLowerCase()]?.label}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({row}: any) => {
          const isExecuted = row?.original?.status == distributionJobStatus.executed.status;
          const isScheduled = row?.original?.status == distributionJobStatus.scheduled.status;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <MoreVertical className="w-5 h-5 cursor-pointer" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-white border-0 shadow-md"
              >
                {isExecuted ? <DropdownMenuItem
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    dispatch(setDistributionJobDtl({...row?.original, budgetType: activeStatusTab, isSimulate: false}))
                    navigate(`${row?.original?.distributionJobId}`)
                  }}
                  disabled={row?.original?.status == distributionJobStatus.superseded.status}
                >
                  <Eye className="w-4 h-4" />
                  View Run Details
                </DropdownMenuItem> : <></>}
                {activeStatusTab == ACTUALS ? 
                  <>
                    {isExecuted ? <></> : 
                      <>
                        <DropdownMenuItem
                          className="flex items-center gap-2"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            selectedJobId.current = row.original?.distributionJobId
                            isEdit.current = true;
                            setOpenAddDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuLabel className="flex items-center gap-2">
                          <div
                            className="flex items-center space-x-2"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <Switch
                              id="active"
                              onCheckedChange={() => {
                                updateInfo({
                                  communityId: communityData?.communityId,
                                  jobId: row.original?.distributionJobId,
                                });
                              }}
                              checked={row.original?.active}
                              disabled={isUpdatePending}
                            />
                            <Label htmlFor="active">
                              {"Active"}
                            </Label>
                          </div>
                        </DropdownMenuLabel>
                      </>
                    }
                    {isScheduled ? <>
                      <DropdownMenuItem
                        className="flex items-center gap-2"
                        disabled={isRunJobPending}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          runJobInfo({
                            communityId: communityData?.communityId,
                            jobId : row.original?.distributionJobId 
                          });
                        }}
                      >
                        <MessageSquareText className="w-4 h-4" />
                        Run Job
                      </DropdownMenuItem>
                      {activeFrequencyTab != FREQUENCY_REAL_TIME ? <DropdownMenuItem
                        className="flex items-center gap-2"
                        disabled={isRunJobPending}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          dispatch(setDistributionJobDtl({...row?.original, budgetType: activeStatusTab, isSimulate: true}))
                          navigate(`${row?.original?.distributionJobId}`)
                        }}
                      >
                        <Box className="w-4 h-4" />
                        Simulate
                      </DropdownMenuItem> : <></>}
                    </> : <></>}
                  </> : <></>}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ]
  }, [activeStatusTab]);

  const statusTabs = useMemo(() => {
    if (activeFrequencyTab == FREQUENCY_REAL_TIME) {
      return [...plannedTab, ...actualTab]
    } else {
      return [...actualTab]
    }
  }, [activeFrequencyTab]);

  useEffect(() => {
    if (activeFrequencyTab == FREQUENCY_REAL_TIME) {
      setActiveStatusTab(PLANNED)
    } else {
      setActiveStatusTab(ACTUALS)
    }
  }, [activeFrequencyTab]);

  return {
    breadcrumbData,
    title,
    activeFrequencyTab,
    setActiveFrequencyTab,
    onAddJobClick,
    activeStatusTab,
    onStatusTabChange,
    columns,
    isLoadingJobList,
    jobList: jobList?.data,
    openAddDialog,
    setOpenAddDialog,
    selectedJobId: selectedJobId.current,
    isEdit: isEdit.current,
    statusTabs
  };
};

export default useDistributionRunTimeDetails;
